import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessageCircle, Send, CheckCircle, Plus } from "lucide-react";
import { Button } from "../../shared/ui";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPROVER_ROLES = ["admin", "manager", "office_staff", "supervisor"];
const fmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

/**
 * QueryThread — single Q&A thread between approver and worker for a specific
 * timesheet target (shift/travel/reimbursement). Supports multi-turn back-and-forth.
 *
 * Props:
 *   - query: existing thread doc OR null (to compose a new question)
 *   - timesheetId / targetType / targetId / targetLabel: needed to start a new thread
 *   - currentUser: from useAuth — used to decide who can ask vs reply
 *   - onChanged: () => void — fires after create/reply/resolve so parent can refetch
 */
export const QueryThread = ({ query, timesheetId, targetType, targetId, targetLabel, currentUser, onChanged }) => {
  const [thread, setThread] = useState(query || null);
  const [composing, setComposing] = useState(!query);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setThread(query || null); setComposing(!query); }, [query]);

  const isApprover = APPROVER_ROLES.includes(currentUser?.role);
  const isOwner = thread ? thread.user_id === currentUser?.id : false;

  const createThread = async () => {
    const q = text.trim();
    if (!q) { toast.error("Type a question first"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/timesheet-queries`, {
        timesheet_id: timesheetId,
        target_type: targetType || "general",
        target_id: targetId,
        target_label: targetLabel,
        question: q,
      });
      setThread(r.data);
      setComposing(false);
      setText("");
      toast.success("Question sent");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send question");
    } finally { setSaving(false); }
  };

  const reply = async () => {
    const msg = text.trim();
    if (!msg) { toast.error("Type a reply"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/timesheet-queries/${thread.id}/reply`, { message: msg });
      setThread(prev => ({
        ...prev,
        status: r.data.status,
        messages: [...(prev.messages || []), r.data.reply],
      }));
      setText("");
      toast.success("Reply sent");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send reply");
    } finally { setSaving(false); }
  };

  const resolve = async () => {
    if (!thread) return;
    if (!window.confirm("Mark this question as resolved? It stays visible as a record.")) return;
    setSaving(true);
    try {
      await axios.post(`${API}/timesheet-queries/${thread.id}/resolve`);
      setThread(prev => ({ ...prev, status: "resolved" }));
      toast.success("Marked resolved");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resolve");
    } finally { setSaving(false); }
  };

  // Status badge
  const statusBadge = (s) => {
    if (s === "resolved") return <span className="text-[10px] uppercase font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">Resolved</span>;
    if (s === "answered") return <span className="text-[10px] uppercase font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Worker replied</span>;
    return <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Awaiting reply</span>;
  };

  // ----- Composing a brand-new thread -----
  if (composing) {
    if (!isApprover) return null;
    return (
      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded space-y-2" data-testid="query-compose">
        <textarea
          className="w-full text-xs p-2 border border-blue-200 rounded resize-none focus:outline-none focus:border-blue-500"
          rows={2}
          placeholder={`Ask the worker about ${targetLabel || targetType || "this entry"}…`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="query-compose-text"
        />
        <div className="flex justify-end gap-1">
          <button
            type="button"
            onClick={() => setText("")}
            className="text-[11px] text-slate-500 hover:text-slate-700 px-2"
          >Clear</button>
          <Button size="sm" onClick={createThread} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="query-send">
            <Send className="w-3 h-3 mr-1" /> {saving ? "Sending..." : "Ask"}
          </Button>
        </div>
      </div>
    );
  }

  if (!thread) return null;

  const canPostMore = thread.status !== "resolved" && (isApprover || isOwner);

  return (
    <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded space-y-2" data-testid={`query-thread-${thread.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <MessageCircle className="w-3.5 h-3.5 text-slate-500" />
          <span className="font-semibold text-slate-700">{thread.target_label || thread.target_type}</span>
        </div>
        {statusBadge(thread.status)}
      </div>
      <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
        {(thread.messages || []).map((m, i) => {
          const fromApprover = APPROVER_ROLES.includes(m.role);
          return (
            <div key={i} className={`text-xs p-1.5 rounded ${fromApprover ? "bg-blue-100 text-blue-900" : "bg-green-100 text-green-900"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold">{m.by_name || "User"}</span>
                <span className="text-[10px] opacity-70">{fmt(m.at)}</span>
              </div>
              <p className="whitespace-pre-wrap break-words">{m.message}</p>
            </div>
          );
        })}
      </div>

      {canPostMore && (
        <div className="space-y-1">
          <textarea
            className="w-full text-xs p-1.5 border border-slate-300 rounded resize-none focus:outline-none focus:border-blue-400"
            rows={2}
            placeholder={isApprover ? "Follow-up question or note…" : "Type your reply…"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid={`query-reply-text-${thread.id}`}
          />
          <div className="flex justify-end gap-1">
            {isApprover && (
              <Button size="sm" variant="ghost" onClick={resolve} disabled={saving} data-testid={`query-resolve-${thread.id}`}>
                <CheckCircle className="w-3 h-3 mr-1" /> Mark resolved
              </Button>
            )}
            <Button size="sm" onClick={reply} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`query-reply-${thread.id}`}>
              <Send className="w-3 h-3 mr-1" /> {saving ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * AskQuestionButton — small inline button that opens a QueryThread compose box
 * next to a shift/travel/reimbursement row.
 */
export const AskQuestionButton = ({ timesheetId, targetType, targetId, targetLabel, currentUser, existingThreads, onChanged }) => {
  const [showCompose, setShowCompose] = useState(false);
  const isApprover = APPROVER_ROLES.includes(currentUser?.role);
  if (!isApprover) return null;

  const matching = (existingThreads || []).filter(t =>
    t.target_type === targetType && (targetId ? t.target_id === targetId : !t.target_id)
  );

  if (showCompose && matching.length === 0) {
    return (
      <QueryThread
        timesheetId={timesheetId}
        targetType={targetType}
        targetId={targetId}
        targetLabel={targetLabel}
        currentUser={currentUser}
        onChanged={() => { setShowCompose(false); onChanged?.(); }}
      />
    );
  }

  return (
    <>
      {matching.length === 0 && (
        <button
          type="button"
          onClick={() => setShowCompose(true)}
          className="text-[11px] font-bold uppercase text-blue-600 hover:text-blue-800 hover:underline px-2 py-1 inline-flex items-center gap-1"
          data-testid={`ask-question-${targetType}-${targetId || "any"}`}
        >
          <MessageCircle className="w-3 h-3" /> Ask
        </button>
      )}
      {matching.map(t => (
        <QueryThread key={t.id} query={t} currentUser={currentUser} onChanged={onChanged} />
      ))}
    </>
  );
};

export default QueryThread;
